import React, { useState } from "react";
import Modal from "#components/modals/Modal";
import { MONTHS } from "#utils/constants";
import { capitalize } from "#utils/utils";
import Button from "#components/helpers/Button";
import { useDispatch } from "react-redux";
import { updateReadingInstances } from "#redux/slices/userBookSlice";

const ReadingInstances = ({ userBook, setUserBookData }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState({ month: "", day: "", year: "" });
  const [endDate, setEndDate] = useState({ month: "", day: "", year: "" });
  const [readingInstances, setReadingInstances] = useState(userBook?.reading_instances || []);
  const [toDelete, setToDelete] = useState([]);
  const [showError, setShowError] = useState(false);

  const handleOpenModal = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setReadingInstances(userBook.reading_instances || []);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setShowForm(false);
    setStartDate({ month: "", day: "", year: "" });
    setEndDate({ month: "", day: "", year: "" });
    setToDelete([]);
    setShowError(false);
    setIsModalOpen(false);
  };

  const handleSaveAdded = () => {
    if (!endDate.month || !endDate.day || !endDate.year) {
      setShowError("Must include Finished Date month, day, and year");
      return;
    }

    if (
      (startDate.month || startDate.day || startDate.year) &&
      (!startDate.month || !startDate.day || !startDate.year)
    ) {
      setShowError("Must include month, day, and year");
      return;
    }

    const formattedStartDate =
      startDate.month && startDate.day && startDate.year
        ? `${MONTHS.indexOf(startDate.month) + 1}/${startDate.day}/${startDate.year}`
        : null;

    const formattedEndDate =
      endDate.month && endDate.day && endDate.year
        ? `${MONTHS.indexOf(endDate.month) + 1}/${endDate.day}/${endDate.year}`
        : null;

    const today = new Date();
    const tomorrow = new Date(today.getTime() + 86400000);
    const endDateObj = new Date(formattedEndDate);

    // give buffer for intl date issues...
    if (endDateObj > tomorrow) {
      setShowError("Finished Date cannot be in the future");
      return;
    }

    const startDateObj = new Date(formattedStartDate);

    if (startDateObj > endDateObj) {
      setShowError("Start Date cannot be after Finished Date");
      return;
    }

    const data = {
      to_add: {
        started_reading_at: formattedStartDate,
        finished_reading_at: formattedEndDate,
      },
    };

    dispatch(updateReadingInstances({ userBookUuid: userBook.uuid, data }))
      .unwrap()
      .then((payload) => setUserBookData(payload));
    handleCloseModal();
  };

  const handleSaveDeleted = () => {
    const data = {
      to_delete: toDelete,
    };
    dispatch(updateReadingInstances({ userBookUuid: userBook.uuid, data }))
      .unwrap()
      .then((payload) => setUserBookData(payload));
    handleCloseModal();
  };

  const onDelete = (id) => {
    setReadingInstances(readingInstances.filter((instance) => instance.id !== id));
    setToDelete([...toDelete, id]);
  };

  const renderAddForm = () => {
    const years = Array.from({ length: 40 }, (_, i) => new Date().getFullYear() - i);

    const getDaysInMonth = (month, year) => {
      return new Date(year || new Date().getFullYear(), month, 0).getDate();
    };

    const handleSelectChange = (dateType, field, value) => {
      if (dateType === "start") {
        setStartDate((prev) => ({ ...prev, [field]: value }));
      } else {
        setEndDate((prev) => ({ ...prev, [field]: value }));
      }
    };

    const renderDropdown = (options, dateType, field) => {
      const classNames = {
        month: "min-w-[110px]",
        day: "min-w-[70px]",
        year: "min-w-[75px]",
      };
      return (
        <select
          className={`flex text-sm bg-beige border-none px-1.5 py-1.5 focus:border-none focus:outline-none focus:ring-0 ${classNames[field]}`}
          value={dateType === "start" ? startDate[field] : endDate[field]}
          onChange={(e) => handleSelectChange(dateType, field, e.target.value)}
        >
          <option value="">{capitalize(field)}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    };

    const renderDaysDropdown = (dateType) => {
      const month = dateType === "start" ? MONTHS.indexOf(startDate.month) + 1 : MONTHS.indexOf(endDate.month) + 1;
      const year = dateType === "start" ? startDate.year : endDate.year;
      const days = month ? Array.from({ length: getDaysInMonth(month, year) }, (_, i) => i + 1) : [];

      return renderDropdown(days, dateType, "day");
    };

    return (
      <div className="flex flex-col">
        <div>
          <p className="text-sm font-bold mb-1">Start Date (optional)</p>
          <div className="flex justify-between bg-beige rounded-boxy">
            {renderDropdown(MONTHS, "start", "month")}
            {renderDaysDropdown("start")}
            {renderDropdown(years, "start", "year")}
          </div>
        </div>
        <div className="mt-2">
          <p className="text-sm font-bold mb-1">Finished Date</p>
          <div className="flex justify-between bg-beige rounded-boxy">
            {renderDropdown(MONTHS, "end", "month")}
            {renderDaysDropdown("end")}
            {renderDropdown(years, "end", "year")}
          </div>
        </div>
        {showError && <div className="text-red text-sm mt-2">{showError}</div>}
        <Button
          onClick={handleSaveAdded}
          text="Save"
          color="bg-neonBlue self-end mt-3"
          disabled={!endDate.month || !endDate.day || !endDate.year}
        />
      </div>
    );
  };

  const renderReadingInstance = (instance, index) => {
    const noDelete = readingInstances.length === 1 && !["finished", "dnf"].includes(instance.status);
    return (
      <div className="flex flex-col gap-2 w-full" key={index}>
        {instance.current && <div className="text-sm mb-[1px]">Current</div>}
        {!instance.current && <div className="text-sm mb-[1px]">Read {index + 1}</div>}
        <div className="flex flex-row w-full gap-1 items-center">
          <div className="flex flex-col w-full gap-[1px]">
            <div className="flex flex-row gap-1 px-2 items-center bg-beige rounded-tl-boxy rounded-tr-boxy">
              <div className="lg:text-sm min-w-[45px]">Started:</div>
              <div className="lg:text-sm">{instance.started_reading_at}</div>
            </div>

            <div className="flex flex-row gap-1 px-2 items-center bg-beige rounded-bl-boxy rounded-br-boxy">
              <div className="lg:text-sm min-w-[45px]">Finished:</div>
              <div className="lg:text-sm">{instance.finished_reading_at}</div>
            </div>
          </div>

          <button
            disabled={instance.current || noDelete}
            onClick={() => onDelete(instance.id)}
            className={`p-1 ${(instance.current || noDelete) && "opacity-0"}`}
            type="button"
          >
            ×
          </button>
        </div>
        {noDelete && <div className="text-xs text-gray">To edit, add new dates and delete the old</div>}
      </div>
    );
  };

  const renderModal = () => {
    return (
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={<h4>{showForm ? "Add Read Dates" : "Dates Read"}</h4>}
      >
        <div className="w-[300px] flex flex-col gap-3">
          <hr className="text-black mb-2" />
          {!showForm && readingInstances.map((instance, index) => renderReadingInstance(instance, index))}
          {showForm ? (
            renderAddForm()
          ) : (
            <div className="flex flex-row items-center gap-1">
              <Button
                onClick={() => setShowForm(true)}
                text="+ Add"
                color="bg-fuschia"
                disabled={toDelete.length > 0}
              />
              {toDelete.length > 0 && <div className="text-xs text-gray">Save your changes before adding</div>}
            </div>
          )}
          {!showForm && toDelete.length > 0 && (
            <Button onClick={handleSaveDeleted} text="Save" color="bg-neonBlue self-end mt-3" />
          )}
        </div>
      </Modal>
    );
  };

  if (!userBook) return <></>;

  if (!["finished", "dnf"].includes(userBook.status)) return <></>;

  return (
    <>
      <button
        className="bg-pastelOrange mt-1 w-[110px] text-xs px-2 py-[2px] rounded-default shadow-main"
        onClick={handleOpenModal}
        type="button"
      >
        {userBook.dates_read_copy}
      </button>
      {isModalOpen && renderModal()}
    </>
  );
};

export default ReadingInstances;
